/* eslint-disable next/no-img-element -- Fixed-size local SVG icons have no raster variants to optimize. */
'use client';
import { listenMediaQuery } from './browser-capabilities.js';
import { dragHintIcon } from './hint-icons.js';

import Image from 'next/image';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ArrowDown,
  ArrowUpRight,
  Cloud,
  ShieldCheck,
  Code2,
  Network,
} from 'lucide-react';
import Environment from './environment';
import { sampleChapterScroll } from './chapter-scroll.js';
import { isIOSDevice } from './ios-device.js';
import ContactBoard from './contact-board';
import SplashScreen from './splash-screen';
import PhoneTiltControl from './phone-tilt-control';
import BackgroundMusic from './background-music';
import LiquidGlass from './liquid-glass';
import PartnersSection from './partners-section';
import { solutionCardProgress } from './solutions-scroll.js';

const loadDialogs = () => import('./dialogs');
const SiteDialogs = lazy(loadDialogs);
const prefetchDialogs = () => {
  void loadDialogs().catch(() => {});
};

const chapters = [
  { id: 'home', name: 'Welcome' },
  { id: 'solutions', name: 'Solutions' },
  { id: 'products', name: 'Products' },
  { id: 'partners', name: 'Clients' },
  { id: 'about', name: 'About Us' },
  { id: 'contact', name: 'Contact Us' },
  { id: 'moon', name: 'To the Moon' },
];
const solutions = [
  {
    icon: Code2,
    title: "App Development",
    tagline: "We create custom mobile and web apps that offer seamless user experiences, security, and scalability, tailored to your business needs.",
    description: "We create custom mobile and web apps that offer seamless user experiences, security, and scalability, tailored to your business needs.",
    items: ["Custom mobile applications", "Custom web applications", "Seamless user experiences", "Security & scalability"],
    number: "01",
  },
  {
    icon: Cloud,
    title: "Web Development",
    tagline: "We build responsive websites and e-commerce platforms that boost your online presence and drive business growth",
    description: "We build responsive websites and e-commerce platforms that boost your online presence and drive business growth",
    items: ["Responsive websites", "E-commerce platforms", "Online presence", "Business growth"],
    number: "02",
  },
  {
    icon: Network,
    title: "Finance & Management Systems",
    tagline: "We provide automated finance solutions to streamline invoicing, reporting, and payroll, ensuring compliance and accurate financial insights.",
    description: "We provide automated finance solutions to streamline invoicing, reporting, and payroll, ensuring compliance and accurate financial insights.",
    items: ["Automated invoicing", "Financial reporting", "Payroll management", "Compliance & financial insights"],
    number: "03",
  },
  {
    icon: ShieldCheck,
    title: "Security & Maintenance",
    tagline: "We offer security audits, data protection, and regular updates to ensure your systems are secure, optimized, and always performing at their best.",
    description: "We offer security audits, data protection, and regular updates to ensure your systems are secure, optimized, and always performing at their best.",
    items: ["Security audits", "Data protection", "Regular updates", "System optimization & maintenance"],
    number: "04",
  },
];
export default function Home() {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [chapter, setChapter] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const outside = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [menuOpen]);
  const [mapOpen, setMapOpen] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const handleReady = useCallback(() => setReady(true), []);
  const handleError = useCallback(() => {
    setFailed(true);
    setReady(false);
  }, []);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncMotion = () => setReducedMotion(media.matches);
    syncMotion();
    const stopMotionListener = listenMediaQuery(media, syncMotion);
    const solutionsSection = document.getElementById('solutions');
    const solutionsStage =
      solutionsSection?.querySelector<HTMLElement>('.solutions-stage');
    const cards = Array.from(
      solutionsSection?.querySelectorAll<HTMLButtonElement>('.solution-card') ??
        [],
    );
    if (solutionsSection && !failed) solutionsSection.dataset.revealReady = 'true';
    if (failed) {
      document.documentElement.classList.add('browser-static');
      cards.forEach(card => { card.inert = false; card.removeAttribute('aria-hidden'); });
      if (solutionsStage) { solutionsStage.inert = false; solutionsStage.removeAttribute('aria-hidden'); }
    }
    const root = document.documentElement;
    const main = document.getElementById('main-content');
    const chapterElements = chapters.map(({ id }) => document.getElementById(id));
    let worldSectionsActive = root.classList.contains('world-sections-active');
    let layoutDirty = true;
    let frame: number | null = null;
    let lastActive = -1;
    let layout = {
      chapterTops: [] as number[],
      viewportHeight: window.innerHeight,
      scrollDistance: 1,
      solutionsStart: 0,
      solutionsDistance: 1,
    };
    const measureLayout = () => {
      // CSS3D writes transforms every frame. Read flow geometry only after an
      // actual layout change, rather than forcing layout on every scroll tick.
      const viewportHeight = window.innerHeight;
      const stickyTop = solutionsStage && !worldSectionsActive
        ? parseFloat(getComputedStyle(solutionsStage).top) || 0
        : 0;
      layout = {
        chapterTops: chapterElements.map(element => element?.offsetTop ?? Infinity),
        viewportHeight,
        scrollDistance: Math.max(1, root.scrollHeight - viewportHeight),
        solutionsStart: (solutionsSection?.offsetTop ?? 0) - stickyTop,
        solutionsDistance: Math.max(
          1,
          (solutionsSection?.offsetHeight ?? 0) - (solutionsStage?.offsetHeight ?? 0),
        ),
      };
      layoutDirty = false;
    };
    const update = () => {
      frame = null;
      if (layoutDirty) measureLayout();
      const scrollY = isIOSDevice() ? sampleChapterScroll(performance.now(), window.scrollY) : window.scrollY;
      const y = scrollY + layout.viewportHeight * 0.45;
      let active = 0;
      layout.chapterTops.forEach((top, i) => {
        if (top <= y) active = i;
      });
      if (active !== lastActive) {
        lastActive = active;
        setChapter(active);
      }
      if (solutionsSection && solutionsStage && !worldSectionsActive && !failed) {
        const progress = Math.max(
          0,
          Math.min(1, (scrollY - layout.solutionsStart) / layout.solutionsDistance),
        );
        const entered = scrollY >= layout.solutionsStart;
        solutionsStage.inert = !entered;
        solutionsStage.setAttribute('aria-hidden', String(!entered));
        solutionsSection.style.setProperty(
          '--intro-reveal',
          String(
            entered ? (media.matches ? 1 : Math.min(1, progress / 0.08)) : 0,
          ),
        );
        cards.forEach((card, index) => {
          const reveal = entered
            ? media.matches
              ? 1
              : solutionCardProgress(progress, index)
            : 0;
          card.style.setProperty('--card-reveal', String(reveal));
          card.inert = reveal < 0.95;
          card.setAttribute('aria-hidden', String(reveal < 0.05));
        });
      }
      if (progressRef.current)
        progressRef.current.style.transform = `scaleX(${scrollY / layout.scrollDistance})`;
    };
    const scroll = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };
    const invalidateLayout = () => {
      layoutDirty = true;
      scroll();
    };
    // Section flow boxes do not change when the 3D panels animate. Watching
    // them catches font/content/viewport reflow without observing frame writes.
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(invalidateLayout) : null;
    if (main) {
      resizeObserver?.observe(main);
      main.querySelectorAll(':scope > section').forEach(section => resizeObserver?.observe(section));
    }
    if (solutionsStage) resizeObserver?.observe(solutionsStage);
    const worldObserver = new MutationObserver(() => {
      const active = root.classList.contains('world-sections-active');
      if (active !== worldSectionsActive) {
        worldSectionsActive = active;
        invalidateLayout();
      }
    });
    worldObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
    update();
    const stopLayoutListener = listenMediaQuery(media, invalidateLayout);
    window.addEventListener('scroll', scroll, { passive: true });
    window.addEventListener('codex-journey-frame', scroll);
    window.addEventListener('resize', invalidateLayout);
    return () => {
      resizeObserver?.disconnect();
      worldObserver.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      stopMotionListener();
      stopLayoutListener();
      root.classList.remove('browser-static');
      if (solutionsSection) delete solutionsSection.dataset.revealReady;
      window.removeEventListener('scroll', scroll);
      window.removeEventListener('codex-journey-frame', scroll);
      window.removeEventListener('resize', invalidateLayout);
    };
  }, [failed]);

  return (
    <>
      <SplashScreen ready={ready || failed} failed={failed} />
      <BackgroundMusic moon={chapter === chapters.length - 1} prepareMoon={chapter >= chapters.length - 2} />
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <div
        className={`environment-fallback ${ready ? 'is-ready' : ''}`}
        aria-hidden="true"
      />
      {!failed && (
        <Environment
          reducedMotion={reducedMotion}
          onReady={handleReady}
          onError={handleError}
        />
      )}
      <PhoneTiltControl visible={ready && !failed && !reducedMotion && chapter === 0} />
      <div className="scene-vignette" aria-hidden="true" />
      <div className="reading-progress" ref={progressRef} aria-hidden="true" />
      <header
        ref={headerRef}
        className={`site-header glass-surface ${chapter > 0 ? 'scrolled' : ''}`}
      >
        <LiquidGlass />
        <a href="#home" aria-label="Codex home" className="brand">
          <Image
            unoptimized
            src="/codex-logo.svg"
            alt="Codex"
            width="139"
            height="24"
          />
        </a>
        <nav aria-label="Main navigation">
          {chapters.filter(item => !['contact','moon'].includes(item.id)).map((item, i) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              aria-current={chapter === i ? 'location' : undefined}
            >
              {item.name}
            </a>
          ))}
        </nav>
        <div className="header-actions">
          <a href="#contact" className="header-cta glass-surface">
            <LiquidGlass />
            <span>Contact Us</span> <ArrowUpRight size={16} />
          </a>
          <button
            className="mobile-menu-button glass-surface"
            ref={menuButtonRef}
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMenuOpen(open => !open)}
          >
            <LiquidGlass />
            <svg className="menu-toggle-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <path d="M4 6H20" />
              <path d="M4 12H20" />
              <path d="M4 18H20" />
            </svg>
          </button>
        </div>
        <div className="mobile-navigation-dropdown glass-surface" data-open={menuOpen} inert={!menuOpen} aria-hidden={!menuOpen}>
          <LiquidGlass />
          <nav id="mobile-navigation" aria-label="Mobile navigation">
            {chapters.map((item, i) => (
              <a key={item.id} href={`#${item.id}`} onClick={() => setMenuOpen(false)}>
                <span className="menu-chapter-number">0{i + 1}</span>
                <span>{item.name}</span>
              </a>
            ))}
          </nav>
        </div>
      </header>
      <nav className="chapter-nav" aria-label="Explore chapters">
        {chapters.map((item, i) => (
          <a
            href={`#${item.id}`}
            key={item.id}
            className={chapter === i ? 'active' : ''}
            aria-label={`${String(i + 1).padStart(2, '0')} ${item.name}`}
            aria-current={chapter === i ? 'location' : undefined}
          >
            <span className="chapter-nav-label">{item.name}</span>
            <span className="chapter-mark" />
          </a>
        ))}
      </nav>
      <main id="main-content">
        <section
          className="chapter hero"
          id="home"
          aria-labelledby="hero-title"
        >
          <div className="hero-content">
            <h1 id="hero-title">
              The Place Where
              <br />
              <span>Innovation Meets</span>
              <br />
              <span>Precision</span>
            </h1>
          </div>
          <span className="environment-drag-hint"><span dangerouslySetInnerHTML={{__html: dragHintIcon}} /><span>Drag to explore</span></span>
          <div className="hero-bottom">
            <a className="scroll-cue" href="#solutions">
              <span className="scroll-circle glass-surface">
                <LiquidGlass />
                <ArrowDown size={16} />
              </span>
              <span>SCROLL TO EXPLORE</span>
            </a>
          </div>
        </section>
        <section
          className="chapter solutions-chapter"
          id="solutions"
          aria-labelledby="solutions-title"
        >
          <div className="chapter-inner solutions-stage">
            <div className="section-top solutions-introduction">
              <h2 id="solutions-title" className="solutions-portal-title">Solutions</h2>
            </div>
            <div className="solutions-grid">
              {solutions.map((solution, i) => {
                const Icon = solution.icon;
                return (
                  <button
                    key={solution.title}
                    className="solution-card glass-surface"
                    data-solution-index={i}
                    onPointerEnter={prefetchDialogs}
                    onFocus={prefetchDialogs}
                    onClick={() => setSelected(i)}
                  >
                    <LiquidGlass />
                    <div className="solution-card-top">
                      <Icon size={30} strokeWidth={1.2} />
                      <span>{solution.number}</span>
                    </div>
                    <h3>{solution.title}</h3>
                    <p>{solution.tagline}</p>
                    <span className="solution-card-link">
                      Explore solution <ArrowUpRight size={18} />
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="section-footnote">
              <span className="status-dot" /> Individual expertise. One
              connected ecosystem.
            </p>
          </div>
        </section>
        <section className="chapter marquee-chapter" id="innovation" aria-label="A future of innovation">
          <div className="innovation-marquee">
            {[0, 1].map(row => (
              <div className={`innovation-glass-row ${row ? 'reverse' : ''}`} key={row}>
                <div className="innovation-track">
                  {[0, 1, 2, 3].map(copy => <span key={copy} aria-hidden={row > 0 || copy > 0 ? true : undefined}>Get Ready to Embrace the Future of Innovation—Where Cutting-Edge Ideas Meet Limitless Possibilities!<img src="/brand/codex-icon.svg" alt="" width="48" height="36" loading="lazy" decoding="async" /></span>)}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="chapter products-chapter" id="products" aria-labelledby="products-title">
          <div className="products-introduction"><h2 id="products-title">Products</h2></div>
        </section>
        <section className="chapter products-showcase-chapter" id="products-showcase" aria-label="Our products">
          <div className="products-panel">
            <p className="eyebrow">BUILT BY CODEX</p>
            <h2>Software that moves your business forward.</h2>
            <div className="products-grid">
              {[
                ['01', 'Postal and Delivery System', 'Manages mail and package tracking, automates logistics, and optimizes delivery routes.'],
                ['02', 'Accounting System', 'Manages financial transactions, invoicing, payroll, and tax calculations.'],
                ['03', 'Currency Exchange System', 'Handles real-time currency conversion, rate management, and transaction tracking.'],
                ['04', 'Restaurant Menu Management', 'Interactive digital menus with QR code access, easy updates, and POS integration for a modern dining experience.'],
              ].map(([number, title, description]) => (
                <article className="product-card solution-card glass-surface" key={number}>
                  <LiquidGlass />
                  <div className="solution-card-top"><Code2 size={30} strokeWidth={1.2}/><span>{number}</span></div>
                  <h3>{title}</h3><p>{description}</p>
                  {number !== '04' && <Image unoptimized width={1179} height={2556} className="static-device-preview" src={number === '01' ? '/screens/postal-mobile-light.webp' : number === '02' ? '/screens/crm-mobile-light.webp' : '/screens/hawalla-mobile-light.webp'} alt={`${title} screen`} loading="lazy" />}
                </article>
              ))}
            </div>
          </div>
        </section>
        <PartnersSection />
        <>
        <section className="chapter about-chapter" id="about" aria-labelledby="about-title">
          <div className="about-introduction"><h2 id="about-title">About Us</h2></div>
        </section>
        <section className="chapter about-story-chapter" id="about-story" aria-labelledby="about-story-title">
          <div className="about-story-panel">
            <div className="about-story-visual" aria-hidden="true">
              <div className="about-orbit"/><div className="about-orbit inner"/>
              <img src="/brand/codex-icon.svg" alt="" width="48" height="36" loading="lazy" decoding="async"/>
              <span>INNOVATION / PRECISION</span>
            </div>
            <div className="about-story-copy">
              <p className="eyebrow">THE PEOPLE BEHIND THE CODE</p>
              <h2 id="about-story-title">Who we are<span>.</span></h2>
              <p className="about-lead">At Codex, we offer expert web and app development, advanced finance and management systems, and comprehensive IT services.</p>
              <p className="about-purpose">Our focus is on delivering tailored, high-quality solutions that drive business efficiency and growth.</p>
              <div className="about-expertise"><span>Web &amp; apps</span><span>Finance &amp; management</span><span>IT services</span></div>
              <a href="#contact" className="about-conversation">Let’s build what’s next <ArrowUpRight size={20}/></a>
            </div>
          </div>
        </section>
        </>
        <section
          className="chapter contact-chapter"
          id="contact"
          aria-labelledby="contact-title"
        >
          <ContactBoard onOpenMap={() => setMapOpen(true)} onPrefetchMap={prefetchDialogs} />

        </section>
        <section className="chapter moon-chapter" id="moon" aria-labelledby="moon-title">
          <div className="moon-copy" aria-hidden="true">
            <p className="moon-eyebrow">BEYOND THE EXPECTED</p>
            <h2 id="moon-title">We Will Fly Your Business<br /><span>To The Moon</span></h2>
          </div>
        </section>
      </main>
      {(selected !== null || mapOpen) && (
        <Suspense
          fallback={<output className="dialog-loading">Opening…</output>}
        >
          <SiteDialogs
            mapOpen={mapOpen}
            setMapOpen={setMapOpen}
            selected={selected}
            setSelected={setSelected}
            solutions={solutions}
          />
        </Suspense>
      )}
    </>
  );
}
